/**
 * USE ORDENES - Hooks para gestión de órdenes, exámenes, perfiles y cotizaciones
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { queryKeys } from "@/lib/query-client";
import {
  getOrden,
  createOrden,
  updateOrden,
  deleteOrden,
  deactivateOrden,
  activateOrden,
  cambiarEstadoOrden,
  darDeBajaOrden,
  getOrdenExamenes,
  getReciboPdf,
  pagarOrden,
  anularPagoOrden,
  anularPagoEspecifico,
  getPagosOrden,
  marcarCortesia,
  getExamenTipo,
  createExamenTipo,
  updateExamenTipo,
  deleteExamenTipo,
  deactivateExamenTipo,
  activateExamenTipo,
  buscarExamenesTipo,
  getPerfil,
  createPerfil,
  updatePerfil,
  deletePerfil,
  deactivatePerfil,
  activatePerfil,
  buscarPerfiles,
  getCotizacion,
  createCotizacion,
  deleteCotizacion,
  deactivateCotizacion,
  activateCotizacion,
  convertirCotizacion,
  getValoresReferencia,
  createValorReferencia,
  updateValorReferencia,
  deleteValorReferencia,
  type OrdenCreateData,
  type OrdenUpdateData,
  type ExamenTipoCreateData,
  type PerfilExamenCreateData,
  type ValorReferenciaCreateData,
  type CotizacionCreateData,
  type CotizacionConvertirData,
  type EstadoOrden,
  type PagarOrdenData,
} from "@/services/ordenes";

// Re-export functions and constants for useServerDataTable
export {
  getOrdenes,
  getExamenesTipo,
  getPerfiles,
  getCotizaciones,
  getOrdenExamenesList,
} from "@/services/ordenes";

// Re-export types
export type {
  ExamenTipo,
  ExamenTipoListItem,
  ExamenTipoCreateData,
  AreaLaboratorio,
  TipoResultado,
  PerfilExamen,
  PerfilExamenListItem,
  PerfilExamenCreateData,
  ValorReferencia,
  ValorReferenciaCreateData,
  Orden,
  OrdenListItem,
  OrdenCreateData,
  OrdenUpdateData,
  OrdenExamen,
  OrdenExamenListItem,
  EstadoOrden,
  PrioridadOrden,
  TipoEntrega,
  TipoServicio,
  EstadoOrdenExamen,
  Cotizacion,
  CotizacionListItem,
  CotizacionCreateData,
  CotizacionConvertirData,
  CotizacionExamen,
  EstadoCotizacion,
  PagoOrden,
  PagoOrdenResult,
  PagarOrdenData,
  TipoPagoOrden,
} from "@/services/ordenes";

interface MutationCallbacks {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface OrdenMutationCallbacks {
  onSuccess?: (orden: import("@/services/ordenes").Orden) => void;
  onError?: (error: Error) => void;
}

// =============================================================================
// QUERIES - Órdenes
// =============================================================================

export function useOrden(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.ordenes.detail(id),
    queryFn: () => getOrden(id),
    enabled: options?.enabled ?? true,
  });
}

export function useOrdenExamenes(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...queryKeys.ordenes.detail(id), "examenes"],
    queryFn: () => getOrdenExamenes(id),
    enabled: options?.enabled ?? true,
  });
}

// =============================================================================
// MUTATIONS - Órdenes
// =============================================================================

export function useCreateOrden(callbacks?: OrdenMutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OrdenCreateData) => createOrden(data),
    onSuccess: (orden) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      callbacks?.onSuccess?.(orden);
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useUpdateOrden(callbacks?: OrdenMutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: OrdenUpdateData }) => updateOrden(id, data),
    onSuccess: (orden) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      callbacks?.onSuccess?.(orden);
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeleteOrden(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteOrden(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeactivateOrden(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deactivateOrden(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useActivateOrden(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => activateOrden(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useCambiarEstadoOrden(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, estado }: { id: number; estado: EstadoOrden }) =>
      cambiarEstadoOrden(id, estado),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDarDeBajaOrden(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) => darDeBajaOrden(id, motivo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// QUERIES - ExamenTipo
// =============================================================================

export function useExamenTipo(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.examenesTipo.detail(id),
    queryFn: () => getExamenTipo(id),
    enabled: options?.enabled ?? true,
  });
}

export function useBuscarExamenesTipo(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.examenesTipo.search(query),
    queryFn: () => buscarExamenesTipo(query),
    enabled: (options?.enabled ?? true) && query.length >= 2,
  });
}

// =============================================================================
// MUTATIONS - ExamenTipo
// =============================================================================

export function useCreateExamenTipo(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ExamenTipoCreateData) => createExamenTipo(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.examenesTipo.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useUpdateExamenTipo(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ExamenTipoCreateData> }) =>
      updateExamenTipo(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.examenesTipo.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeleteExamenTipo(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteExamenTipo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.examenesTipo.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeactivateExamenTipo(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deactivateExamenTipo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.examenesTipo.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useActivateExamenTipo(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => activateExamenTipo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.examenesTipo.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// QUERIES - Perfiles
// =============================================================================

export function usePerfil(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.perfiles.detail(id),
    queryFn: () => getPerfil(id),
    enabled: options?.enabled ?? true,
  });
}

export function useBuscarPerfiles(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.perfiles.search(query),
    queryFn: () => buscarPerfiles(query),
    enabled: (options?.enabled ?? true) && query.length >= 2,
  });
}

// =============================================================================
// MUTATIONS - Perfiles
// =============================================================================

export function useCreatePerfil(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PerfilExamenCreateData) => createPerfil(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.perfiles.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useUpdatePerfil(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<PerfilExamenCreateData> }) =>
      updatePerfil(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.perfiles.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeletePerfil(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deletePerfil(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.perfiles.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeactivatePerfil(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deactivatePerfil(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.perfiles.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useActivatePerfil(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => activatePerfil(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.perfiles.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// QUERIES - ValoresReferencia
// =============================================================================

export function useValoresReferencia(examenTipoId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.valoresReferencia.list({ examen_tipo: examenTipoId }),
    queryFn: () => getValoresReferencia({ examen_tipo: examenTipoId, page_size: 100, page: 1 }),
    enabled: (options?.enabled ?? true) && examenTipoId > 0,
  });
}

// =============================================================================
// MUTATIONS - ValoresReferencia
// =============================================================================

export function useCreateValorReferencia(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ValorReferenciaCreateData) => createValorReferencia(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.valoresReferencia.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useUpdateValorReferencia(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ValorReferenciaCreateData> }) =>
      updateValorReferencia(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.valoresReferencia.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeleteValorReferencia(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteValorReferencia(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.valoresReferencia.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// QUERIES - Cotizaciones
// =============================================================================

export function useCotizacion(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.cotizaciones.detail(id),
    queryFn: () => getCotizacion(id),
    enabled: options?.enabled ?? true,
  });
}

// =============================================================================
// MUTATIONS - Cotizaciones
// =============================================================================

export function useCreateCotizacion(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CotizacionCreateData) => createCotizacion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cotizaciones.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeleteCotizacion(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteCotizacion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cotizaciones.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeactivateCotizacion(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deactivateCotizacion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cotizaciones.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useActivateCotizacion(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => activateCotizacion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cotizaciones.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useConvertirCotizacion(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CotizacionConvertirData }) =>
      convertirCotizacion(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cotizaciones.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// RECIBO PDF
// =============================================================================

export function useGenerarReciboPdf() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: number) => getReciboPdf(id),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    },
    onError: () => {
      toast.error(t("ordenes.recibos.error"));
    },
  });
}

// =============================================================================
// PAGO DIRECTO
// =============================================================================

export function usePagosOrden(ordenId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.ordenes.pagos(ordenId),
    queryFn: () => getPagosOrden(ordenId),
    enabled: (options?.enabled ?? true) && ordenId > 0,
    staleTime: 0,
    refetchOnMount: true,
  });
}

interface PagoMutationCallbacks {
  onSuccess?: (result: import("@/services/ordenes").PagoOrdenResult) => void;
  onError?: (error: Error) => void;
}

export function usePagarOrden(callbacks?: PagoMutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PagarOrdenData }) => pagarOrden(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      callbacks?.onSuccess?.(result);
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useAnularPagoOrden(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => anularPagoOrden(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useAnularPagoEspecifico(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ordenId, pagoId }: { ordenId: number; pagoId: number }) =>
      anularPagoEspecifico(ordenId, pagoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// CORTESÍA
// =============================================================================

export function useMarcarCortesia(callbacks?: OrdenMutationCallbacks) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, autorizado_por }: { id: number; autorizado_por: string }) =>
      marcarCortesia(id, autorizado_por),
    onSuccess: (orden) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      toast.success(t("ordenes.cortesia.toast.success"));
      callbacks?.onSuccess?.(orden);
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}
